import React from 'react';
import { Facebook, Instagram, Link2, MessageCircle, Copy, X, Gift } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useApp } from '../../context/AppContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// X (formerly Twitter) icon component
const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const ShareModal = ({ isOpen, onClose, product }) => {
  const { user, isAuthenticated } = useApp();
  
  if (!product) return null;

  // Create tracked share URL with user's referral.
  // Uses the backend OG preview endpoint so Facebook/X/WhatsApp show the product title, price & photo.
  const baseShareUrl = `https://mintedbuysell.com.au/api/share/product/${product.id}`;
  const shareUrl = isAuthenticated && user?.id 
    ? `${baseShareUrl}?ref=${user.id}` 
    : baseShareUrl;
  const shareTitle = product.title;
  const shareText = `Check out ${product.title} for $${product.price} on Minted!`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard!');
    // Track share event
    if (window.fbq) window.fbq('track', 'Share', { content_name: product.title });
    if (window.gtag) window.gtag('event', 'share', { method: 'copy_link', content_type: 'product', item_id: product.id });
  };

  // Native share (mobile)
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        if (window.fbq) window.fbq('track', 'Share', { content_name: product.title });
        onClose();
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyLink();
        }
      }
    } else {
      copyLink();
    }
  };

  // SMS share
  const shareViaSMS = () => {
    const smsUrl = `sms:?body=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
    window.location.href = smsUrl;
    if (window.fbq) window.fbq('track', 'Share', { content_name: product.title, method: 'sms' });
    onClose();
  };

  const shareToSocial = (platform) => {
    let url = '';
    
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'x':
        url = `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'whatsapp':
        url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
        break;
      case 'messenger':
        url = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(shareUrl)}&app_id=YOUR_APP_ID&redirect_uri=${encodeURIComponent(shareUrl)}`;
        break;
      default:
        copyLink();
        return;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
    onClose();
  };

  // For Instagram, we copy the link since Instagram doesn't have a direct share URL
  const shareToInstagram = () => {
    copyLink();
    toast.success('Link copied! Open Instagram to share.');
  };

  // For TikTok
  const shareToTikTok = () => {
    copyLink();
    toast.success('Link copied! Open TikTok to share.');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Share & Earn</DialogTitle>
        </DialogHeader>
        
        {/* Earn Credit Banner */}
        {isAuthenticated && (
          <div className="flex items-center gap-2 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg mb-3">
            <Gift className="w-8 h-8 text-amber-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800 text-sm">Earn 50¢ credit!</p>
              <p className="text-xs text-amber-600">When someone clicks your shared link</p>
            </div>
          </div>
        )}
        
        {/* Product Preview */}
        <div className="flex gap-3 p-3 bg-gray-50 rounded-lg mb-4">
          <img
            src={product.images?.[0] || 'https://via.placeholder.com/64'}
            alt={product.title}
            className="w-16 h-16 rounded-lg object-cover"
          />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 text-sm truncate">{product.title}</p>
            <p className="text-minted-600 font-bold">${product.price}</p>
          </div>
        </div>

        {/* Share Options */}
        <div className="grid grid-cols-4 gap-3 mb-4">
          <button
            onClick={() => shareToSocial('facebook')}
            className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
              <Facebook className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs text-gray-600">Facebook</span>
          </button>
          
          <button
            onClick={shareToInstagram}
            className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-full flex items-center justify-center">
              <Instagram className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs text-gray-600">Instagram</span>
          </button>
          
          <button
            onClick={shareToTikTok}
            className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </div>
            <span className="text-xs text-gray-600">TikTok</span>
          </button>
          
          <button
            onClick={() => shareToSocial('whatsapp')}
            className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs text-gray-600">WhatsApp</span>
          </button>
        </div>

        {/* Native Share Button (Mobile) */}
        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            onClick={nativeShare}
            className="w-full flex items-center justify-center gap-2 p-3 mb-4 rounded-lg bg-minted-600 hover:bg-minted-700 text-white font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            Share Now
          </button>
        )}

        {/* More Options */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <button
            onClick={() => shareToSocial('x')}
            className="flex items-center justify-center gap-2 p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <XIcon className="w-5 h-5 text-black" />
            <span className="text-sm text-gray-700">X</span>
          </button>
          
          <button
            onClick={() => shareToSocial('messenger')}
            className="flex items-center justify-center gap-2 p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.17.16.13.26.35.27.57l.05 1.78c.04.57.61.94 1.13.71l1.98-.87c.17-.08.36-.1.55-.06.91.23 1.88.35 2.88.35 5.64 0 10-4.13 10-9.7C22 6.13 17.64 2 12 2zm5.89 7.73l-2.88 4.57c-.45.72-1.45.87-2.08.32l-2.29-1.72a.75.75 0 0 0-.9 0l-3.09 2.34c-.41.31-.95-.18-.68-.62l2.88-4.57c.45-.72 1.45-.87 2.08-.32l2.29 1.72a.75.75 0 0 0 .9 0l3.09-2.34c.41-.31.95.18.68.62z"/>
            </svg>
            <span className="text-sm text-gray-700">Messenger</span>
          </button>

          <button
            onClick={shareViaSMS}
            className="flex items-center justify-center gap-2 p-3 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="text-sm text-gray-700">SMS</span>
          </button>
        </div>

        {/* Copy Link */}
        <div className="flex gap-2">
          <div className="flex-1 flex items-center bg-gray-100 rounded-lg px-3 overflow-hidden">
            <Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="flex-1 bg-transparent border-0 text-sm text-gray-600 p-2 outline-none truncate"
            />
          </div>
          <Button onClick={copyLink} className="bg-minted-600 hover:bg-minted-700">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;